// @flow
import * as React from 'react';

import { selectCartProductOwner } from '@gandi/cart';
import { Corporate } from '@gandi/icons';
import { useTranslator } from '@gandi/translate';

import { AlertBlock } from 'src/Reshaped/AlertBlock';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { useFeatureFlag } from 'src/hooks/useFeatureFlag';

import { useCorporateAlert } from '../hooks/useCorporateAlert';
import { useIncompatibleBundleGrid } from '../hooks/useIncompatibleBundleGrid';
import { useOwnershipDisclaimer } from '../hooks/useOwnershipDisclaimer';
import { useResellerOnlyAlert } from '../hooks/useResellerOnlyAlert';

export function InfoBlock(): React.MixedElement | null {
  const { __, formatDate } = useTranslator();

  const displayNewAccountChoice = useFeatureFlag('FF_SHOP_ACCOUNT_CHOICE');

  const { hasTransfer, hasCloud, hasRenew, hasChangeOwner } =
    useOwnershipDisclaimer();
  const { hasIncompatibleBundleGrid } = useIncompatibleBundleGrid();
  const { hasCorporateProduct, isCorporateOrg, date, sharingId } =
    useCorporateAlert();
  const { ownerMustReseller } = useResellerOnlyAlert();

  const productOwner = useAppSelector(selectCartProductOwner);

  if (!displayNewAccountChoice) return null;

  const alertConfigs: {
    [key: string]: {
      condition: () => ?boolean,
      title?: string,
      messages: () => Array<string | { __html: string }>,
      icon?: React.Node,
    },
  } = {
    ownershipDisclaimer: {
      condition: () => !!hasTransfer || !!hasRenew || !!hasCloud,
      messages: () => {
        const messages: Array<string | { __html: string }> = [];
        if (hasTransfer && !hasChangeOwner) {
          messages.push(
            __(
              'The owner of the transferred domains cannot be modified as per registry policy. The owner will remain the same after the transfer.',
            ),
          );
        }
        if (hasTransfer && hasChangeOwner) {
          messages.push(
            __(
              'The owner of the transferred domains can be specified afterwards.',
            ),
          );
        }
        if (hasRenew) {
          messages.push(__('Owners will not change for renewed products.'));
        }
        if (hasCloud) {
          messages.push(
            __(
              "Cloud resources will be credited to the selected owner's Cloud account.",
            ),
          );
        }
        return messages;
      },
    },

    corporateAlert: {
      condition: () => hasCorporateProduct,
      icon: <Corporate />,
      title: isCorporateOrg
        ? __('This organization has an active Corporate Pack until %(date)s.', {
            date: date ? formatDate(date, 'LL') : __('Unknown date'),
          })
        : __(
            'Your cart contains domain names with extensions reserved for Corporate services members.',
          ),
      messages: () => {
        const messages: Array<string | { __html: string }> = [];

        if (!isCorporateOrg) {
          messages.push(
            __(
              'A Corporate Pack is added to your cart to allow you to buy them. You will define ' +
                'the desired subscription length in the next step. You can register as many domains as ' +
                'you want during this period.',
            ),
          );
        } else {
          messages.push(
            __(
              'Your cart contains domain names with extensions reserved for Corporate services members.',
            ),
            __(
              'You can register as many domains as you want during this period.',
            ),
          );
        }
        messages.push(
          __(
            'Any domain names you aquire remains yours and can always be renewed, even after the ' +
              'temporary subscription expires.',
          ),
        );
        return messages;
      },
    },

    incompatibleBundleGrid: {
      condition: () => hasIncompatibleBundleGrid,
      messages: () => [
        __(
          'Because this organization already has a special price rate on domain name purchases, additional domain bundle prices do not apply.',
        ),
      ],
    },

    taxAlert: {
      condition: () => !!productOwner && productOwner !== sharingId,
      messages: () => [
        __(
          'When you purchase the delivery of a service for someone else (the customer who receives the service), you must ensure that the applicable VAT rules are followed. This means that the person who receives the service constitutes the beneficial owner of the service subject to VAT in the member state in which they reside.',
        ),
      ],
    },

    resellerOnlyAlert: {
      condition: () => ownerMustReseller,
      messages: () => [
        __(
          'Your cart contains products that were bought at Gandi through a reseller. Only resellers can be billed for shopping carts containing such products.',
        ),
      ],
    },
  };

  return (
    <>
      {Object.entries(alertConfigs).map(([key, { condition, ...config }]) => {
        if (!condition()) return null;

        const messages = config.messages();

        return <AlertBlock {...config} key={key} messages={messages} />;
      })}
    </>
  );
}
